"use client";

/**
 * CortexAISetupWizard — shared credential onboarding gate.
 *
 * Renders the "Connect your AI provider" flow used by both:
 *   - apps/flowstate-start (Cortex drawer + settings page + /cortex/ surface)
 *
 * The wizard is intentionally self-contained: it validates keys with the brain,
 * persists them via SECURITY DEFINER RPCs, and reports completion to the host.
 *
 * Implementation note: this component intentionally avoids @flowstate/ui so the
 * @flowstate/cortex-auth package stays free of UI-library layering inversions.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { AICredentialStatus, UserAISettings } from "@flowstate/types";
import {
  checkCredentials,
  isGeminiKeyFormat,
  isOpenRouterKeyFormat,
} from "@flowstate/ai";
import {
  Bot,
  KeyRound,
  Sparkles,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

export type SetupMode = "gate" | "inline" | "compact";

export interface CortexAISetupWizardProps {
  /** Supabase client for RPC calls */
  supabase: SupabaseClient;
  /** Current user's access token (needed for brain key-check) */
  accessToken: string;
  /** Supabase project URL */
  supabaseUrl: string;
  /** Optional Google OAuth state / handler */
  isGoogleConnected?: boolean;
  onConnectGoogle?: () => void | Promise<void>;
  /** True when the deployed brain has a platform OpenRouter key configured */
  allowOpenRouterPlatform?: boolean;
  /** Current local settings, if known */
  settings?: UserAISettings;
  /** Called after a successful credential save */
  onSettingsChanged?: (settings: Partial<UserAISettings>) => void;
  /** Called when the user has a usable credential and dismisses the gate */
  onComplete?: () => void;
  /** Visual density */
  mode?: SetupMode;
}

const GEMINI_KEY_HINT = "AIza...";
const OPENROUTER_KEY_HINT = "sk-or-v1-...";

export function CortexAISetupWizard({
  supabase,
  accessToken,
  supabaseUrl,
  isGoogleConnected,
  onConnectGoogle,
  allowOpenRouterPlatform = false,
  settings,
  onSettingsChanged,
  onComplete,
  mode = "gate",
}: CortexAISetupWizardProps) {
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (settings?.provider === "openrouter") return "openrouter";
    return "gemini";
  });

  const setActiveTabWithFeedback = useCallback((tab: string) => {
    setActiveTab(tab);
    setSaveSuccess(null);
    setSubmitError(null);
  }, []);

  const [geminiKey, setGeminiKey] = useState("");
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [openrouterKey, setOpenrouterKey] = useState("");
  const [showOpenrouterKey, setShowOpenrouterKey] = useState(false);
  const [usePlatformOpenRouter, setUsePlatformOpenRouter] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const isCompact = mode === "compact";

  const currentStatus: AICredentialStatus = useMemo(() => {
    if (isGoogleConnected) return "gemini_oauth";
    if (settings?.geminiApiKey || settings?.geminiByokOnServer) return "gemini_byok";
    if (settings?.openrouterApiKey || settings?.openrouterApiKeyOnServer) {
      return "openrouter_byok";
    }
    if (settings?.provider === "openrouter" && allowOpenRouterPlatform) {
      return "openrouter_platform";
    }
    return settings?.aiCredentialStatus ?? "none";
  }, [isGoogleConnected, settings, allowOpenRouterPlatform]);

  const isReady = currentStatus !== "none";

  useEffect(() => {
    if (isReady && mode === "gate" && onComplete) {
      const t = setTimeout(onComplete, 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isReady, mode, onComplete]);

  const handleSaveGeminiKey = useCallback(async () => {
    const key = geminiKey.trim();
    if (!isGeminiKeyFormat(key)) {
      setSubmitError(
        `Gemini keys start with "AIza" and are at least 20 characters.`,
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSaveSuccess(null);

    try {
      const check = await checkCredentials({
        supabaseUrl,
        accessToken,
        provider: "gemini",
        key,
        signal: AbortSignal.timeout(20_000),
      });

      if (!check.ok) {
        setSubmitError(check.error ?? "Key validation failed.");
        return;
      }

      const { data, error } = await supabase.rpc("set_my_gemini_api_key" as never, {
        p_key: key,
      } as never);

      if (error || data !== true) {
        setSubmitError(error?.message ?? "Failed to save Gemini key.");
        return;
      }

      setGeminiKey("");
      setSaveSuccess("Gemini API key connected and saved.");
      onSettingsChanged?.({
        provider: "gemini",
        geminiApiKey: key,
        geminiByokOnServer: true,
        aiCredentialStatus: "gemini_byok",
        useOwnKey: true,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [geminiKey, supabaseUrl, accessToken, supabase, onSettingsChanged]);

  const handleSaveOpenRouterKey = useCallback(async () => {
    if (usePlatformOpenRouter && allowOpenRouterPlatform) {
      onSettingsChanged?.({
        provider: "openrouter",
        aiCredentialStatus: "openrouter_platform",
        useOwnKey: false,
      });
      onComplete?.();
      return;
    }

    const key = openrouterKey.trim();
    if (!isOpenRouterKeyFormat(key)) {
      setSubmitError(
        `OpenRouter keys start with "sk-or-v1-" and are at least 40 characters.`,
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSaveSuccess(null);

    try {
      const check = await checkCredentials({
        supabaseUrl,
        accessToken,
        provider: "openrouter",
        key,
        signal: AbortSignal.timeout(20_000),
      });

      if (!check.ok) {
        setSubmitError(check.error ?? "Key validation failed.");
        return;
      }

      const { data, error } = await supabase.rpc("set_my_openrouter_api_key" as never, {
        p_key: key,
      } as never);

      if (error || data !== true) {
        setSubmitError(error?.message ?? "Failed to save OpenRouter key.");
        return;
      }

      setOpenrouterKey("");
      setSaveSuccess("OpenRouter API key connected and saved.");
      onSettingsChanged?.({
        provider: "openrouter",
        openrouterApiKey: key,
        openrouterApiKeyOnServer: true,
        aiCredentialStatus: "openrouter_byok",
        useOwnKey: true,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    usePlatformOpenRouter,
    allowOpenRouterPlatform,
    openrouterKey,
    supabaseUrl,
    accessToken,
    supabase,
    onSettingsChanged,
    onComplete,
  ]);

  const handleConnectGoogle = useCallback(async () => {
    try {
      setSaveSuccess(null);
      setSubmitError(null);
      await onConnectGoogle?.();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  }, [onConnectGoogle]);

  const clearFeedback = useCallback(() => {
    setSaveSuccess(null);
    setSubmitError(null);
  }, []);

  const handleClearGeminiKey = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSaveSuccess(null);
    try {
      const { data, error } = await supabase.rpc("clear_my_gemini_api_key" as never);
      if (error || data !== true) {
        setSubmitError(error?.message ?? "Failed to remove Gemini key.");
        return;
      }
      setSaveSuccess("Gemini API key removed.");
      onSettingsChanged?.({
        provider: undefined,
        geminiApiKey: undefined,
        geminiByokOnServer: false,
        aiCredentialStatus: "none",
        useOwnKey: false,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [supabase, onSettingsChanged]);

  const handleClearOpenRouterKey = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSaveSuccess(null);
    try {
      const { data, error } = await supabase.rpc("clear_my_openrouter_api_key" as never);
      if (error || data !== true) {
        setSubmitError(error?.message ?? "Failed to remove OpenRouter key.");
        return;
      }
      setSaveSuccess("OpenRouter API key removed.");
      onSettingsChanged?.({
        provider: undefined,
        openrouterApiKey: undefined,
        openrouterApiKeyOnServer: false,
        aiCredentialStatus: "none",
        useOwnKey: false,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [supabase, onSettingsChanged]);

  const statusBanner = useMemo(() => {
    if (isReady) {
      return (
        <div
          role="alert"
          className="relative w-full rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
            <div>
              <div className="font-medium">Cortex is ready</div>
              <div className="text-muted-foreground">{statusLabel(currentStatus)}</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        role="alert"
        className="relative w-full rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <div>
            <div className="font-medium">Connect an AI provider to use Cortex</div>
            <div className="opacity-90">
              Chat, specialists, notebooks, and agent tools need your own API access.
              Free tiers are available for both Gemini and OpenRouter.
            </div>
          </div>
        </div>
      </div>
    );
  }, [currentStatus, isReady]);

  const wrapperClass = isCompact
    ? "space-y-4"
    : "mx-auto w-full max-w-xl space-y-6 p-6";

  return (
    <div className={wrapperClass}>
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-3">
          <Bot className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            {isReady ? "AI Provider Connected" : "Set Up Cortex AI"}
          </h2>
          <p className="text-sm text-muted-foreground">
            Your data, your quota, your bill.
          </p>
        </div>
      </div>

      {statusBanner}

      <div className="w-full">
        <div className="grid w-full grid-cols-2 rounded-md bg-muted p-1">
          <button
            type="button"
            onClick={() => setActiveTabWithFeedback("gemini")}
            className={tabTriggerClass(activeTab === "gemini")}
            aria-selected={activeTab === "gemini"}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Gemini
          </button>
          <button
            type="button"
            onClick={() => setActiveTabWithFeedback("openrouter")}
            className={tabTriggerClass(activeTab === "openrouter")}
            aria-selected={activeTab === "openrouter"}
          >
            <KeyRound className="mr-2 h-4 w-4" />
            OpenRouter
          </button>
        </div>

        {activeTab === "gemini" && (
          <div className="space-y-4 pt-4">
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-medium flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Google Gemini
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Recommended for the most reliable Cortex experience.
                  </p>
                </div>

                {isGoogleConnected ? (
                  <div
                    role="alert"
                    className="relative w-full rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
                      <div>
                        <div className="font-medium">Google connected</div>
                        <div className="text-muted-foreground">
                          Your Google account is connected and Cortex can use your Gemini quota.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleConnectGoogle}
                      disabled={!onConnectGoogle}
                      className="inline-flex h-10 w-full items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Connect Google Account
                    </button>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">
                          or paste a key
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor="gemini-api-key"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Gemini API Key
                  </label>
                  <KeyInput
                    id="gemini-api-key"
                    type={showGeminiKey ? "text" : "password"}
                    placeholder={GEMINI_KEY_HINT}
                    value={geminiKey}
                    onChange={setGeminiKey}
                    onFocus={clearFeedback}
                    show={showGeminiKey}
                    onToggleShow={() => setShowGeminiKey((v) => !v)}
                    disabled={isGoogleConnected}
                    ariaLabel={showGeminiKey ? "Hide key" : "Show key"}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveGeminiKey}
                      disabled={!geminiKey.trim() || isSubmitting || isGoogleConnected}
                      className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    >
                      {isSubmitting && activeTab === "gemini" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Save
                    </button>
                    {currentStatus === "gemini_byok" && (
                      <button
                        type="button"
                        onClick={handleClearGeminiKey}
                        disabled={isSubmitting}
                        className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                      >
                        Remove key
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Get a free key at{" "}
                    <a
                      href="https://aistudio.google.com/app/apikey"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      Google AI Studio <ExternalLink className="h-3 w-3" />
                    </a>
                    . Free tier available — no credit card required.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "openrouter" && (
          <div className="space-y-4 pt-4">
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-medium flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-primary" />
                    OpenRouter
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Use Claude, DeepSeek, GPT, Llama and 200+ models through one API key.
                  </p>
                </div>

                {allowOpenRouterPlatform && (
                  <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                    <input
                      id="use-platform-openrouter"
                      type="checkbox"
                      checked={usePlatformOpenRouter}
                      onChange={(e) => setUsePlatformOpenRouter(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-primary text-primary"
                    />
                    <div className="space-y-1">
                      <label
                        htmlFor="use-platform-openrouter"
                        className="text-sm font-medium leading-none"
                      >
                        Use FlowState&apos;s OpenRouter access
                      </label>
                      <p className="text-xs text-muted-foreground">
                        No key required — inference is handled by the platform OpenRouter account.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor="openrouter-api-key"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    OpenRouter API Key
                  </label>
                  <KeyInput
                    id="openrouter-api-key"
                    type={showOpenrouterKey ? "text" : "password"}
                    placeholder={OPENROUTER_KEY_HINT}
                    value={openrouterKey}
                    onChange={setOpenrouterKey}
                    onFocus={clearFeedback}
                    show={showOpenrouterKey}
                    onToggleShow={() => setShowOpenrouterKey((v) => !v)}
                    disabled={usePlatformOpenRouter}
                    ariaLabel={showOpenrouterKey ? "Hide key" : "Show key"}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveOpenRouterKey}
                      disabled={
                        (usePlatformOpenRouter
                          ? false
                          : !openrouterKey.trim()) || isSubmitting
                      }
                      className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    >
                      {isSubmitting && activeTab === "openrouter" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      {usePlatformOpenRouter ? "Continue" : "Save"}
                    </button>
                    {currentStatus === "openrouter_byok" && !usePlatformOpenRouter && (
                      <button
                        type="button"
                        onClick={handleClearOpenRouterKey}
                        disabled={isSubmitting}
                        className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                      >
                        Remove key
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Get a key at{" "}
                    <a
                      href="https://openrouter.ai/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      openrouter.ai/keys <ExternalLink className="h-3 w-3" />
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {submitError && (
        <div
          role="alert"
          className="relative w-full rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>{submitError}</div>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div
          role="alert"
          className="relative w-full rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-300"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
            <div>{saveSuccess}</div>
          </div>
        </div>
      )}

      {isReady && onComplete && mode === "gate" && (
        <button
          type="button"
          onClick={onComplete}
          className="inline-flex h-10 w-full items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Continue to Cortex
        </button>
      )}
    </div>
  );
}

function tabTriggerClass(active: boolean): string {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
  return active
    ? `${base} bg-background text-foreground shadow-sm`
    : `${base} text-muted-foreground hover:bg-muted/50 hover:text-foreground`;
}

function statusLabel(status: AICredentialStatus): string {
  switch (status) {
    case "gemini_oauth":
      return "Connected via Google Gemini OAuth";
    case "gemini_byok":
      return "Connected with your Gemini API key";
    case "openrouter_byok":
      return "Connected with your OpenRouter API key";
    case "openrouter_platform":
      return "Using FlowState OpenRouter access";
    case "none":
    default:
      return "No AI provider connected";
  }
}

interface KeyInputProps {
  id?: string;
  type: "text" | "password";
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  show: boolean;
  onToggleShow: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}

function KeyInput({
  id,
  type,
  placeholder,
  value,
  onChange,
  onFocus,
  show,
  onToggleShow,
  disabled,
  ariaLabel,
}: KeyInputProps) {
  return (
    <div className="relative flex-1">
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        disabled={disabled}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-10"
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-0 top-0 flex h-full items-center px-3 text-muted-foreground hover:text-foreground"
        aria-label={ariaLabel}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
